import {
  GetQuestionReviewTaskByIdDocument,
  useDeleteSubtaskMutation,
} from '@exam-management/graphql';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';

import { useQuestionReviewTaskDetails } from '../../providers';

export interface RemoveReviewerModalReviewer {
  id: string;
}

export interface RemoveReviewerModalProps {
  open: boolean;
  reviewer: RemoveReviewerModalReviewer | null;
  onClose: () => void;
}

export default function RemoveReviewerModal(props: Readonly<RemoveReviewerModalProps>) {
  const { open, reviewer, onClose } = props;

  const { showPromiseSnackbar } = useSnackbar();
  const { taskDetails } = useQuestionReviewTaskDetails();

  const [deleteSubtask, { loading: isRemovingReviewer }] = useDeleteSubtaskMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetQuestionReviewTaskByIdDocument,
        variables: {
          input: {
            taskId: taskDetails.id,
          },
        },
      },
    ],
  });

  const handleClose = () => {
    if (isRemovingReviewer) return;

    onClose();
  };

  const handleRemoveReviewer = async () => {
    if (!reviewer) {
      throw new Error('Reviewer is required');
    }

    const { data } = await deleteSubtask({
      variables: {
        deleteSubtaskInput: {
          subtaskId: reviewer.id,
        },
      },
    });

    const result = data?.wflDeleteSubtask;

    if (!result?.success) {
      throw new Error(result?.message || 'Failed to remove reviewer');
    }

    onClose();
  };

  const handleSubmit = () => {
    showPromiseSnackbar({
      message: {
        success: 'Reviewer has been removed',
        error: 'Failed to remove reviewer',
        pending: 'Removing reviewer...',
      },
      promise: handleRemoveReviewer,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <DialogTitle onClose={handleClose}>Remove Reviewer</DialogTitle>
      <DialogContent>คุณแน่ใจหรือไม่ว่าต้องการลบผู้รีวิวออกจากงานนี้</DialogContent>
      <DialogActions>
        <Button
          variant='outlined'
          color='secondary'
          onClick={handleClose}
          disabled={isRemovingReviewer}
        >
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSubmit} loading={isRemovingReviewer}>
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  );
}
